/**
 * 264714 — EVERY TAB, AND THE LAYER IT BECAME.
 *
 * ⚠️ WHY THIS EXISTS. Erez, 29 Aug 2026: "I want to see all the tabs that they
 * have, and how are they mapped into our database, and where is the logic and
 * everything. Because I don't understand what you're showing me." He had spent
 * an evening expecting to see Outform's Executive Summary in the product and
 * getting a reconciliation table instead — which proves the engine is right and
 * is a different artefact entirely. Neither of us could see the whole shape.
 *
 * This is the whole shape. Read left to right: their file, our ingest, our
 * database, our logic, our screens.
 *
 * ── THE FOUR THINGS THE LAYOUT IS ASSERTING ────────────────────────────────
 *
 * 1 · THEIR OWN TAB BAR DECIDES THE GROUPING, not us. Five of the nineteen
 *     sheets are EMPTY section dividers Outform typed themselves — `INPUTS →`,
 *     `Est. Worksheets →`, `Commercial Output →`, `LOOKUP →>`. That is where
 *     the front-facing / working split comes from, and we did not have to
 *     invent a taxonomy. The column is ordered the way their tab bar is.
 *
 * 2 · ⭐ THE BOTTOM UP WORKSHEET IS A PROGRAM, NOT A DOCUMENT — and the
 *     evidence is unusually clean. 545 cells: 453 live formulas, 77 labels,
 *     and NINE numbers a human typed, every one of them the digit 1 (on/off
 *     multipliers beside the cost lines). Zero business data. That is why its
 *     wire is dashed and lands in `costing.py` rather than in a table: the
 *     sheet became CODE, not rows. Nothing about it can be a screen.
 *
 * 3 · ⭐ THEIR EXECUTIVE SUMMARY HAS ZERO TYPED CELLS and 271 formulas. It is
 *     pure output, so nothing from it is stored — we recompute it. It is drawn
 *     red on the right because the page does not exist yet: their sheet
 *     publishes 179 numbers and we show the values behind 12.
 *
 * 4 · ⛔ `estimate_lines` IS DRAWN RED IN THE DATABASE and has no wire out of
 *     it. It holds 49 rows carrying `extended_cost` — a plain stored column,
 *     computed once at load and frozen — written by the loader retired on
 *     29 Aug. So two differently-derived answers to what this job costs sit in
 *     one database with nothing recording which is authoritative. Drawing it
 *     with no outgoing wire is the point: it feeds a view we no longer trust.
 *
 * Every count on this canvas was read from `raw.cell` and the domain tables on
 * 29 Aug 2026, load run 2212114a, workbook sha256 95a56d405b02907f… Nothing
 * here is remembered.
 */
// @refresh reset
import { Canvas, leaf, box, type Diagram } from "./c4";

const D: Diagram = (() => {
  // ── 1 · THEIR WORKBOOK, in their own tab order ────────────────────────────
  // The five dividers are not drawn as sheets — they ARE the four containers.
  const workbook = box("wb", "264714 · Morphe Sephora 2Bay  [19 sheets]", "col", [
    box("wb-front", "the front of the document", "col", [
      leaf("s-pr", "PR", "[3 embedded images]", "Image"),
      leaf("s-setup", "Setup", "[6 typed]", "ClipboardList"),
      leaf("s-exec", "Executive Summary", "[0 typed · 271 fx]", "FileText"),
    ], { gap: 8 }),
    box("wb-inputs", "INPUTS →", "col", [
      leaf("s-bom", "BOM", "[33 typed]", "Boxes"),
      leaf("s-other", "Other Costs", "[21 typed]", "Package"),
      leaf("s-labor", "Labor", "[10 typed]", "Wrench"),
      leaf("s-freight", "Freight", "[1 typed]", "Truck"),
      leaf("s-storage", "Storage Workbook", "[4 typed]", "Warehouse"),
    ], { gap: 8 }),
    box("wb-est", "Est. Worksheets →", "col", [
      leaf("s-bu", "Bottom Up Worksheet", "[453 fx · 9 typed, all = 1]", "Calculator"),
    ], { gap: 8 }),
    box("wb-comm", "Commercial Output →", "col", [
      leaf("s-brk", "Breakout", "[3,400 blank fx]", "TrendingUp"),
      leaf("s-bus", "Bottom Up-sales", "[466 fx]", "Calculator"),
      leaf("s-bus2", "Bottom Up-sales (2)", "[0 fx — dead paste]", "FileX", "gate"),
    ], { gap: 8 }),
    box("wb-look", "LOOKUP →>", "col", [
      leaf("s-assum", "Assumptions", "[49 typed]", "Table"),
      leaf("s-cx", "Complexity", "[8 typed]", "Gauge"),
      leaf("s-oth", "Other", "[17 labels only]", "Minus"),
    ], { gap: 8 }),
  ], { gap: 12 });

  // ── 2 · THE INGEST ────────────────────────────────────────────────────────
  const ingest = box("ing", "outform-ingest  [python]", "col", [
    leaf("i-land", "LAND", "[every cell · COPY]", "Download"),
    leaf("i-interp", "INTERPRET", "[8 sheet adapters]", "Split"),
    leaf("i-valid", "VALIDATE", "[fails closed]", "ShieldCheck"),
    leaf("i-promote", "PROMOTE", "[ON CONFLICT · no DELETE]", "Upload"),
  ], { gap: 10 });

  // ── 3 · THE DATABASE ──────────────────────────────────────────────────────
  const db = box("db", "outform-db :5432  [postgres 17]", "col", [
    box("db-raw", "raw — replayable, append-only", "col", [
      leaf("t-cell", "raw.cell", "[6,483 rows]", "Database"),
      leaf("t-run", "raw.load_run", "[sha256 · provenance]", "FileClock"),
    ], { gap: 8 }),
    box("db-facts", "public — typed facts, this job", "col", [
      leaf("t-bom", "bom_line", "[16]", "Rows3"),
      leaf("t-lab", "labor_task", "[9]", "Rows3"),
      leaf("t-oth", "other_cost", "[12]", "Rows3"),
      leaf("t-frt", "freight_line", "[3]", "Rows3"),
      leaf("t-sto", "storage_line", "[5]", "Rows3"),
      leaf("t-in", "job_input", "[20]", "Rows3"),
    ], { gap: 7 }),
    box("db-ref", "public — shared, tenant-level", "col", [
      leaf("t-rate", "rate_card", "[46]", "Table"),
      leaf("t-cx", "complexity_factor", "[15]", "Table"),
    ], { gap: 8 }),
    leaf("t-assets", "job_assets", "[3 PR pages]", "Paperclip"),
    leaf("t-est", "estimate_lines", "[49 · STORED TOTALS]", "AlertTriangle", "gate"),
  ], { gap: 12 });

  // ── 4 · THE LOGIC ─────────────────────────────────────────────────────────
  const app = box("app", "outform-app :8090", "col", [
    leaf("a-cost", "costing.py", "[their Bottom Up, in code]", "Cpu"),
    leaf("a-recon", "reconcile.py", "[13 of 13 · theirs vs ours]", "Scale"),
  ], { gap: 10 });

  // ── 5 · THE SCREENS ───────────────────────────────────────────────────────
  const ui = box("ui", "the console  [react]", "col", [
    box("ui-live", "live today", "col", [
      leaf("u-files", "Files", "[the PR]", "Paperclip"),
      leaf("u-over", "Overview", "[setup · complexity]", "LayoutDashboard"),
      leaf("u-cost", "Cost", "[BOM · labour · other]", "Receipt"),
      leaf("u-log", "Logistics", "[freight · storage]", "Truck"),
      leaf("u-recon", "Reconciliation", "[the proof]", "Scale"),
    ], { gap: 8 }),
    box("ui-gap", "the gap", "col", [
      leaf("u-exec", "Executive Summary", "[179 numbers — NOT BUILT]", "FileText", "gate"),
      leaf("u-price", "Pricing", "[9-rung ladder — thin]", "TrendingUp", "gate"),
    ], { gap: 8 }),
  ], { gap: 12 });

  return {
    key: "o264714",
    title: "264714 — EVERY TAB, AND THE LAYER IT BECAME",
    subtitle:
      "Read 29 Aug 2026 from raw.cell · load run 2212114a · sha256 95a56d405b02907f… · input sheets became DATA, the calculator became CODE, the report is COMPUTED",
    cols: [
      { root: workbook, align: "top" },
      { root: ingest, align: "center" },
      { root: db, align: "top" },
      { root: app, align: "center" },
      { root: ui, align: "top" },
    ],
    // Every wire reaches an ADJACENT column. c4.tsx paints the wire SVG first
    // and the boxes over it, so a wire crossing a container is swallowed by
    // that container's fill — the failure this engine's own skill documents.
    wires: [
      // Their sheets into the extractor. One wire per section, not per sheet:
      // the extractor has no idea which sheet it is reading, and drawing five
      // identical wires would imply it does.
      { a: "wb-front", b: "i-land", as: "r", bs: "l", label: "openpyxl" },
      { a: "wb-inputs", b: "i-land", as: "r", bs: "l", label: "openpyxl" },
      { a: "wb-comm", b: "i-land", as: "r", bs: "l", label: "openpyxl" },
      { a: "wb-look", b: "i-land", as: "r", bs: "l", label: "openpyxl" },

      // ⭐ The calculator does NOT become data. It became code, and it is the
      // one wire on this canvas that skips the database entirely.
      { a: "wb-est", b: "i-land", as: "r", bs: "l", label: "rules only", dashed: true },

      // Landing, then promotion.
      { a: "i-land", b: "db-raw", as: "r", bs: "l", label: "COPY" },
      { a: "i-promote", b: "db-facts", as: "r", bs: "l", label: "upsert" },
      { a: "i-promote", b: "db-ref", as: "r", bs: "l", label: "upsert" },
      { a: "i-promote", b: "t-assets", as: "r", bs: "l", label: "assets.save" },

      // The engine reads facts. Nothing computed is stored.
      { a: "db-facts", b: "a-cost", as: "r", bs: "l", label: "SQL" },
      { a: "db-ref", b: "a-cost", as: "r", bs: "l", label: "SQL" },
      { a: "db-raw", b: "a-recon", as: "r", bs: "l", label: "SQL · their side", dashed: true },

      // Out to the screens.
      { a: "a-cost", b: "ui-live", as: "r", bs: "l", label: "HTTPS /api" },
      { a: "a-recon", b: "u-recon", as: "r", bs: "l", label: "HTTPS /api" },
      { a: "a-cost", b: "ui-gap", as: "r", bs: "l", label: "to build", red: true, dashed: true },
    ],
    legend: [
      { style: "solid", label: "a typed fact travels this way — read, landed, promoted, computed, shown" },
      { style: "dash", label: "not data — the calculator became code, and the frozen workbook is read only to compare against" },
      { style: "red", label: "does not exist yet · or is a stored total we no longer trust" },
    ],
    padBottom: 70,
  };
})();

/** One picture: their 19 tabs, and what each one became on our side. */
export function Outform264714LayersDiagram() {
  return <Canvas d={D} mode="contrast" />;
}
